import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TermsConditionsDialogComponent } from '@serviceplan/ui-kit';
import { CaptchaPopupComponent } from '@thehubz/client/captcha';
import { ClientEnvironment, ENVIRONMENT, NotificationService, emailValidator, matchingPasswords } from '@thehubz/client/core';
import { CreateRegistrationUserDto, UserRegistrationApiService } from '@thehubz/client/data-access-api';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'lh-sign-up',
  templateUrl: './sign-up.component.html',
  styleUrls: ['./sign-up.component.scss'],
})
export class SignUpComponent implements OnInit {
  signUpFormGroup: UntypedFormGroup;
  logoUrl!: string;

  passwordInputType: 'password' | 'text' = 'password';
  confirmPasswordInputType: 'password' | 'text' = 'password';
  passwordVisible = false;
  confirmPasswordVisible = false;

  constructor(
    private formBuilder: FormBuilder,
    private dialog: MatDialog,
    private readonly userRegistrationService: UserRegistrationApiService,
    private readonly notificationService: NotificationService,
    private readonly router: Router,
    @Inject(ENVIRONMENT) private readonly env: ClientEnvironment,
  ) {
    this.signUpFormGroup = this.formBuilder.group(
      {
        firstName: ['', [Validators.required, Validators.minLength(3)]],
        lastName: ['', [Validators.required, Validators.minLength(2)]],
        email: ['', [Validators.required, Validators.email, emailValidator]],
        confirmEmail: ['', [Validators.required, Validators.email, emailValidator]],
        password: ['', [Validators.required, this.customPasswordValidator()]],
        confirmPassword: ['', [Validators.required]],
        termsOfService: [null, Validators.requiredTrue],
        agreement: [null],
      },
      {
        validator: [matchingPasswords('password', 'confirmPassword'), matchingPasswords('email', 'confirmEmail')],
      },
    );
  }

  ngOnInit(): void {
    this.logoUrl = 'assets/logos/brand-logo.svg';

    CaptchaPopupComponent.loadAWSScript();
  }

  customPasswordValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const value = control.value;
      const hasUpperCase = /[A-Z]/.test(value);
      const hasLowerCase = /[a-z]/.test(value);
      const hasNumber = /\d/.test(value);
      const hasSpecialChar = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(value);
      const minLength = value.length >= 8;

      // Consider valid if the control value is empty (""), the "required" validator will check this.
      if (value === '' || (hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar && minLength)) {
        return null;
      } else {
        return {
          passwordStrength: true,
        };
      }
    };
  }

  signUpBtnClick() {
    if (this.signUpFormGroup.valid) {
      this.dialog
        .open(CaptchaPopupComponent, {
          width: '500px',
        })
        .afterClosed()
        .subscribe({
          next: (result) => {
            if (result && result.success) {
              this.signUpUser();
            }
          },
        });
    }
  }

  /**
   * Call the API to sign up the user
   */
  private signUpUser() {
    const userDetails: CreateRegistrationUserDto = {
      firstName: this.signUpFormGroup.get('firstName')?.value,
      lastName: this.signUpFormGroup.get('lastName')?.value,
      email: this.signUpFormGroup.get('email')?.value,
      password: this.signUpFormGroup.get('password')?.value,
    };

    this.userRegistrationService.userRegistrationControllerCreateUser(userDetails).subscribe({
      next: (response) => {
        if (response) {
          this.router.navigate(['/sign-up-success'], {
            queryParams: {
              email: userDetails.email,
            },
          });
        }
      },
      error: (errorRes) => {
        this.notificationService.addNotification({
          message: errorRes.error.message,
          type: 'failure',
          duration: 2000,
        });
      },
    });
  }

  async openTermsOfServiceDialog() {
    if (this.signUpFormGroup.get('termsOfService')?.value) {
      this.signUpFormGroup.get('termsOfService')?.setValue(false);
    }

    const dialogRef = this.dialog.open(TermsConditionsDialogComponent, {
      minHeight: '40rem',
      maxWidth: '40rem',
      data: {
        featureKey: 'registration',
      },
    });

    const result = await firstValueFrom(dialogRef.afterClosed());
    if (result.accepted) {
      this.signUpFormGroup.get('termsOfService')?.setValue(true);
    }
  }

  redirectToSignIn() {
    this.router.navigate(['/login']);
  }

  toggleVisibility(confirmPasswordFlag?: boolean) {
    if (confirmPasswordFlag) {
      this.confirmPasswordVisible = !this.confirmPasswordVisible;
      this.confirmPasswordInputType = this.confirmPasswordVisible ? 'text' : 'password';
    } else {
      this.passwordVisible = !this.passwordVisible;
      this.passwordInputType = this.passwordVisible ? 'text' : 'password';
    }
  }
}
